import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from '../../../store'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { getCurrentSubscriptionDetail } from '../../../modules/subscription/action'
import type { SubscriptionDetailObject } from '../../../modules/subscription/reducer'
import { useStyles } from './SubscriptionDetail.styles'
import { Grid } from '@mui/material'
import { useFlagValue } from '@labrav/flags'
import { useAccountId, useCurrentCloudProvider } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import {
  SubscriptionHeader,
  OfferHeader,
  GcpOfferHeader,
  ContractDetailsSection,
  RenewalDetailsSection,
  RenewalInstallmentsSection,
  BuyerDetailsSection,
  OfferDetailsSection,
  ProductDetailsSection,
  PricingConfigurationSection,
  PaymentScheduleSection,
  EulaDetailsSection,
  CustomMetadataSection,
} from './components'
import {
  getContractDuration,
  getOfferDetailInfo,
  getSubscriptionContractStartDate,
  getSubscriptionRenewalDetailsItem,
} from './subscriptionDetailHelpers'
import type { ContractPdfDownloadButtonProps } from './components/OfferHeader'
import { RenewalDetailsBanners } from '../../../common/components/RenewalDetailsView/RenewalDetailsBanners'
import { getContractEndIso } from '../../../common/utils/renewalDetailsFormat'
import type { ContractEndLike } from '../../../common/utils/renewalDetailsFormat'
import {
  DownloadDetailPdfButton,
  DetailPdfCloud,
  DetailPdfPageType,
} from '../../../common/components/DownloadDetailPdfButton'
import { getCurrentReceivedResaleAuthorization } from '../../../modules/receivedResellerAuthorization/action'

export {
  getContractDuration,
  getOfferDetailInfo,
  getSubscriptionContractStartDate,
}
export type { ContractPdfDownloadButtonProps }

const ContractPdfDownloadButton = ({
  partnerId,
  entityId,
}: ContractPdfDownloadButtonProps) => (
  <DownloadDetailPdfButton
    pageType={DetailPdfPageType.CONTRACT}
    partnerId={partnerId}
    entityId={entityId}
    cloud={DetailPdfCloud.AWS}
    testId="download-contract-pdf"
  />
)

export type SubscriptionDetailProps = {
  isBack?: boolean
  partnerIdFromContext?: string
}

const SubscriptionDetail = ({
  isBack = true,
  partnerIdFromContext,
}: SubscriptionDetailProps) => {
  useSyncFlyoutRouteParams()

  const { value: isRenewalPricingTermEnabled } =
    useFlagValue('renewalPricingTerm')
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const cloudType = useCurrentCloudProvider()
  const emptySubscription = ''
  const { accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || partnerIdFromContext || fallbackAccountId

  const { subscriptionsId } = useParams<{
    subscriptionsId: string
  }>()
  const subscriptionDetail = useSelector<SubscriptionDetailObject>(
    state =>
      state?.subscriptions?.currentSubscription?.[
        subscriptionsId || emptySubscription
      ] || null
  )

  useEffect(() => {
    if (!isEmpty(accountId) && subscriptionsId) {
      dispatch(
        getCurrentSubscriptionDetail(accountId, subscriptionsId, cloudType)
      )
    }
  }, [accountId, subscriptionsId, cloudType, dispatch])

  useEffect(() => {
    if (
      !isEmpty(accountId) &&
      !isEmpty(subscriptionDetail) &&
      cloudType !== 'GCP'
    ) {
      const isCPPOSubscription =
        subscriptionDetail?.subscription?.offerType === 'CPPO'
      if (
        isCPPOSubscription &&
        subscriptionDetail?.cppoDetails?.creationOrigin === 'CP'
      ) {
        const authorizationId =
          subscriptionDetail?.cppoDetails?.labraResaleAuthorizationId
        if (authorizationId) {
          dispatch(
            getCurrentReceivedResaleAuthorization(accountId, authorizationId)
          )
        }
      }
    }
  }, [accountId, subscriptionDetail, cloudType, dispatch])

  const isCPPOSubscription =
    subscriptionDetail?.subscription?.offerType === 'CPPO'

  const isCPViewSubscription =
    subscriptionDetail?.cppoDetails?.creationOrigin === 'CP'

  const isISVViewSubsbcription =
    subscriptionDetail?.cppoDetails?.creationOrigin === 'ISV'

  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )
  const [enteredUplift, setEnteredUplift] = useState<number | null>(null)

  const isGcp = cloudType === 'GCP'

  const offerDetailInfo = isGcp
    ? {
        offerName: undefined,
        createdAt: undefined,
        labraOfferId: undefined,
        cloudOfferId: undefined,
        eulaType: undefined,
        companyName: undefined,
      }
    : getOfferDetailInfo({ subscriptionDetail, isCPPOSubscription })
  const isLoaded = !isEmpty(subscriptionDetail)

  if (!isLoaded) return <></>

  const subscriptionForRenewal = {
    ...subscriptionDetail.subscription,
    enteredUpliftPercent:
      enteredUplift ?? subscriptionDetail.subscription?.enteredUpliftPercent,
  }
  const detailForRenewal = {
    ...subscriptionDetail,
    subscription: subscriptionForRenewal,
  }
  const renewalDetailsItem = getSubscriptionRenewalDetailsItem(detailForRenewal)

  const gcpSections = (
    <>
      <ContractDetailsSection
        subscriptionDetail={subscriptionDetail}
        offerDetailInfo={offerDetailInfo}
        timeZone={timeZone}
        classes={classes}
        cloudType={cloudType}
      />
      <PricingConfigurationSection
        subscriptionDetail={subscriptionDetail}
        cloudType={cloudType}
        timeZone={timeZone}
        classes={classes}
      />
      <PaymentScheduleSection
        subscriptionDetail={subscriptionDetail}
        timeZone={timeZone}
        classes={classes}
        cloudType={cloudType}
      />
      <BuyerDetailsSection
        subscriptionDetail={subscriptionDetail}
        offerDetailInfo={offerDetailInfo}
        classes={classes}
        cloudType={cloudType}
      />
      <OfferDetailsSection
        subscriptionDetail={subscriptionDetail}
        offerDetailInfo={offerDetailInfo}
        timeZone={timeZone}
        classes={classes}
        cloudType={cloudType}
      />
      <EulaDetailsSection
        subscriptionDetail={subscriptionDetail}
        classes={classes}
        cloudType={cloudType}
      />
    </>
  )

  const nonGcpSections = (
    <>
      <ContractDetailsSection
        subscriptionDetail={subscriptionDetail}
        offerDetailInfo={offerDetailInfo}
        timeZone={timeZone}
        classes={classes}
        cloudType={cloudType}
      />

      <BuyerDetailsSection
        subscriptionDetail={subscriptionDetail}
        offerDetailInfo={offerDetailInfo}
        classes={classes}
        cloudType={cloudType}
      />

      <OfferDetailsSection
        subscriptionDetail={subscriptionDetail}
        offerDetailInfo={offerDetailInfo}
        timeZone={timeZone}
        classes={classes}
        cloudType={cloudType}
      />

      <ProductDetailsSection
        subscriptionDetail={subscriptionDetail}
        cloudType={cloudType}
        classes={classes}
      />

      <PricingConfigurationSection
        subscriptionDetail={subscriptionDetail}
        cloudType={cloudType}
        timeZone={timeZone}
        classes={classes}
      />

      <PaymentScheduleSection
        subscriptionDetail={subscriptionDetail}
        timeZone={timeZone}
        classes={classes}
        cloudType={cloudType}
      />

      {isRenewalPricingTermEnabled && (
        <>
          <RenewalDetailsSection
            subscriptionDetail={detailForRenewal}
            classes={classes}
            timeZone={timeZone}
            onSubmitFinalUplift={setEnteredUplift}
          />
          <RenewalInstallmentsSection
            subscriptionDetail={detailForRenewal}
            classes={classes}
          />
        </>
      )}

      <EulaDetailsSection
        subscriptionDetail={subscriptionDetail}
        classes={classes}
        cloudType={cloudType}
      />

      <CustomMetadataSection
        subscriptionDetail={subscriptionDetail}
        classes={classes}
      />
    </>
  )

  const sections = isGcp ? gcpSections : nonGcpSections

  const gcpHeaderOfferInfo = isGcp
    ? {
        companyName: subscriptionDetail?.buyerDetails?.buyerName,
        offerName: subscriptionDetail?.offerDetails?.offerName,
        offerType: subscriptionDetail?.offerDetails?.offerType,
      }
    : offerDetailInfo

  const headerSubscriptionDetail = isGcp
    ? ({ productDetails: {} } as unknown as SubscriptionDetailObject)
    : subscriptionDetail

  return (
    <>
      <SubscriptionHeader
        offerDetailInfo={gcpHeaderOfferInfo}
        subscriptionDetail={headerSubscriptionDetail}
        classes={classes}
        isBack={isBack}
        cloudType={cloudType}
      />
      <div className={classes.pageContainer}>
        <div className={classes.gridContainer}>
          <Grid container spacing={4}>
            <Grid
              className={classes.leftColumn}
              size={{
                sm: 12,
                md: 8,
                lg: 8,
                xl: 8,
              }}
              data-testid={isGcp ? 'left-section-gcp' : 'left-section'}
            >
              {!isGcp && (
                <OfferHeader
                  offerDetailInfo={offerDetailInfo}
                  subscriptionDetail={subscriptionDetail}
                  isCPPOSubscription={isCPPOSubscription}
                  isCPViewSubscription={isCPViewSubscription}
                  isISVViewSubsbcription={isISVViewSubsbcription}
                  subscriptionsId={subscriptionsId || ''}
                  classes={classes}
                  cloudType={cloudType}
                  partnerId={accountId}
                  ContractPdfDownloadButton={ContractPdfDownloadButton}
                />
              )}

              {isGcp && subscriptionDetail && (
                <GcpOfferHeader
                  subscriptionDetail={subscriptionDetail}
                  accountId={accountId}
                  classes={classes}
                />
              )}

              {isRenewalPricingTermEnabled && !isGcp && (
                <RenewalDetailsBanners
                  item={renewalDetailsItem}
                  endIso={getContractEndIso(
                    renewalDetailsItem as ContractEndLike
                  )}
                  timeZone={timeZone}
                  onSubmitUplift={setEnteredUplift}
                />
              )}

              <div className={classes.contentContainer}>{sections}</div>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  )
}

export default SubscriptionDetail
